import { ViewIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertDescription,
    AlertTitle,
    Badge,
    Box,
    Button,
    Center,
    FormControl,
    HStack,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useDisclosure,
    useToast,
    VStack
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FaBox, FaClock, FaEnvelope, FaPhotoVideo, FaPrint, FaShare } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { fetchCouriersBySearch, fetchPickupsByCOurierId, savePickupOrder, transferPickup } from '../../api';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import PopoverId from '../../components/PopoverId';
import TitleHeader from '../../components/TitleHeader';
import { getSelectStyles, pickupBadgeColor, useTableColors } from '../../Utils';

const CourierPickups = () => {
    const { id } = useParams()
    const { isOpen: isTransferModalOpen, onOpen: onOpenTransferModal, onClose: onCloseTransferModal } = useDisclosure();
    const navigate = useNavigate()

    const toast = useToast()
    const [allPickups, setAllPickups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingProcess, setLoadingProcess] = useState(false);
    const [selectedPickup, setSelectedPickup] = useState(null);
    const [error, setError] = useState(null);

    const [courier, setCourier] = useState(null);
    const [pendingChanges, setPendingChanges] = useState(false);

    const [transferCourier, setTransferCourier] = useState(null);
    const [transferAllPickups, setTransferAllPickups] = useState(false);
    const [couriers, setCouriers] = useState([]);

    const {
        rowBgEven,
        rowBgOdd,
        rowHoverBg,
        borderColor,
        textColor,
        theadBg,
        dropOffBg,
        suppliesBg,
        pickedBg,
    } = useTableColors();
    const coolors = useTableColors();

    useEffect(() => {
        refresh()
        fetchCouriersBySearch().then(setCouriers);
    }, []);

    const refresh = () => {
        getPickups();
    }

    const status = (status) => {
        switch (status) {
            case 'PENDING':
                return "Pending";
            case 'PICKED_UP':
                return "Picked Up"
            case 'COMPLETED':
                return 'Received'
            case 'CANCELED':
                return "CANCELED"
            default:
                break;
        }
    }

    const getPickups = async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchPickupsByCOurierId(id);

            if (response.success) {
                const pickups = response.data.pickups;
                const cor = response.data.courier;
                setAllPickups(pickups);
                setCourier(cor)
            } else {
                setError(response.error);
            }
        } catch (error) {
            setError('Failed to load routes');
        }
        setLoading(false);
    };



    function getRequirementsIcons(requirements = {}) {
        const icons = [];


        if (requirements.hasDocs) {
            icons.push(
                <Tooltip label="This pickup has documents/envelopes" placement='top' key="docs">
                    <span><FaEnvelope size={15} /></span>
                </Tooltip>
            );
        }
        if (requirements.hasSupplies) {
            icons.push(
                <Tooltip label="This pickup has supplies" placement='top' key="supplies">
                    <span><FaBox size={15} /></span>
                </Tooltip>
            );
        }
        if (requirements.requestPhoto) {
            icons.push(
                <Tooltip label="This pickup requires a photo" placement='top' key="photo">
                    <span><FaPhotoVideo size={15} /></span>
                </Tooltip>
            );
        }


        return icons.length > 0 ? (
            <span style={{ display: 'flex', gap: '15px', alignItems: 'center', padding: 5 }}>
                {icons}
            </span>
        ) : "N/A";
    }


    const handleDragEnd = (result) => {
        if (!result.destination) return;
        const reordered = Array.from(allPickups);
        const [removed] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, removed);
        // Update each pickup's order to reflect the new positions
        const updatedPickups = reordered.map((pickup, index) => ({
            ...pickup,
            pickupOrder: index,
        }));
        setAllPickups(updatedPickups);
        setPendingChanges(true);
    };


    const handleTransferPickup = async () => {
        try {
            const updateParams = {
                courier: transferCourier.value,
                oldCourierName: courier.name,
                oldCourierId: courier._id,
                newCourierName: transferCourier?.label
            };

            const response = await transferPickup(updateParams, selectedPickup._id);
            if (response.success) {

                setAllPickups(prevPickups => prevPickups.filter(pickup => pickup._id !== selectedPickup._id));

                toast({
                    title: "Great!",
                    description: "Pickup transfered",
                    status: "success",
                    duration: 4000, // duration in milliseconds
                    isClosable: true,
                });
                onCloseTransferModal()

            } else {
                toast({
                    title: "Oops w!",
                    description: response.error,
                    status: "error",
                    duration: 4000, // duration in milliseconds
                    isClosable: true,
                });

            }
        } catch (error) {
            console.error('Error performing search:', error);
        }
    }

    const handleBulkPickupTransfer = async () => {
        try {
            const updateParams = {
                courier: transferCourier.value,
                oldCourierName: courier.name,
                oldCourierId: courier._id,
                newCourierName: transferCourier.label
            };

            const updatePromises = allPickups.map((pickup) =>
                transferPickup(updateParams, pickup._id)
            );

            const responses = await Promise.all(updatePromises);
            const failedUpdates = responses.filter(response => !response.success);

            if (failedUpdates.length === 0) {
                setAllPickups([]);
                toast({
                    title: "Great!",
                    description: "All pickups transferred successfully",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: "Partial Success",
                    description: `${failedUpdates.length} pickups failed to transfer`,
                    status: "warning",
                    duration: 4000,
                    isClosable: true,
                });
            }

            onCloseTransferModal();
        } catch (error) {
            console.error('Error transferring all pickups:', error);
            toast({
                title: "Error",
                description: "Failed to transfer all pickups",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        }
    };


    const saveOrder = async () => {
        try {
            // Update each pickup's pickupOrder based on its current index
            const reorderedPickups = allPickups.map((pickup, index) => ({
                ...pickup,
                pickupOrder: index + 1, // Assuming 1-based order
            }));

            // Send the reordered pickups to the backend
            const response = await savePickupOrder(reorderedPickups); // savePickupOrder is an assumed API function

            if (response.success) {
                setAllPickups(reorderedPickups);  // Update state with new order
                setPendingChanges(false);  // Reset pending changes
                toast({
                    title: "Order Saved",
                    description: "The new order has been successfully saved.",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                throw new Error(response.error);
            }
        } catch (error) {
            toast({
                title: "Error Saving Order",
                description: error.message || "An error occurred while saving the order.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const loadOptions = (inputValue, callback) => {
        const search = inputValue.toLowerCase();
        const filtered = couriers.filter(opt =>
            opt.label.toLowerCase().includes(search) ||
            opt.routeName.toLowerCase().includes(search)
        );
        callback(filtered);
    };


    return (
        <Box maxW="full" mx="auto">
            <VStack spacing={6} align="stretch">
                <TitleHeader
                    title={`${courier?.name} Pickups`}
                    buttonText={"Print"}
                    icon={<FaPrint />}
                    onClick={() => {
                        const newWindow = window.open('', '', 'width=1000,height=800');
                        newWindow.document.write(`
        <html>
          <head>
            <title>Pickups – ${courier?.name}</title>
            <style>
              body { font-family: Tahoma; padding: 40px; }
              h1 { text-align: center; margin-bottom: 24px; }
              table { width: 100%; border-collapse: collapse; margin-top: 16px; }
              thead { background-color: #f0f0f0; }
              th, td { border: 1px solid #ccc; padding: 10px; text-align: left; }
              th { font-weight: bold; }
            </style>
          </head>
          <body>
            <h1>Pickups – ${courier?.name}</h1>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pickup</th>
                  <th>Drop</th>
                  <th>Address</th>
                  <th>Count</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                ${allPickups.map((p, i) => {
                            const pickupAddr = [
                                p.pickupLocation?.address?.address1,
                                p.pickupLocation?.address?.address2,
                                p.pickupLocation?.address?.city,
                                p.pickupLocation?.address?.state,
                                p.pickupLocation?.address?.zip
                            ].filter(Boolean).join(', ');
                            const dropAddr = [
                                p.dropOffLocation?.address?.address1,
                                p.dropOffLocation?.address?.address2,
                                p.dropOffLocation?.address?.city,
                                p.dropOffLocation?.address?.state,
                                p.dropOffLocation?.address?.zip
                            ].filter(Boolean).join(', ');
                            return `
                    <tr>
                      <td>${i + 1}</td>
                      <td>${p.pickupLocation?.name || p.dropOffLocation?.name}</td>
                      <td>${p.dropOffLocation?.name || p.pickupLocation?.name}</td>
                      <td>${pickupAddr || dropAddr}</td>
                      <td></td>
                      <td></td>
                    </tr>
                  `;
                        }).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `);
                        newWindow.document.close();
                        newWindow.focus();
                        newWindow.print();
                        newWindow.close();
                    }}
                />


                {loading ?
                    <Center mt={"20%"}>
                        <Loading />
                    </Center>
                    :
                    <>
                        {error ? (
                            <AccessDenied msg={error} />
                        )
                            :
                            <>
                                {pendingChanges && (
                                    <Alert
                                        status="info"
                                        variant="subtle"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        textAlign="center"
                                        height="100px"
                                    >
                                        <AlertTitle mb={1} fontSize="lg">
                                            Pickups order changed
                                        </AlertTitle>
                                        <AlertDescription>
                                            <Button
                                                isLoading={false}
                                                loadingText={"Saving Order"}
                                                mt={4}
                                                size="sm"
                                                colorScheme="red"
                                                onClick={saveOrder}
                                            >
                                                Save Order
                                            </Button>
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <DragDropContext onDragEnd={handleDragEnd}>
                                    <Table size="sm">
                                        <Thead bg={theadBg}>
                                            <Tr>
                                                <Th>#</Th>
                                                <Th>Pickup</Th>
                                                <Th>Drop</Th>
                                                <Th>Courier</Th>
                                                <Th>Type</Th>
                                                <Th>RQ</Th>
                                                <Th>Route</Th>
                                                <Th>Status</Th>
                                                <Th>SC</Th>
                                                <Th>Created At</Th>
                                                <Th textAlign="right">
                                                    <Button
                                                        ml={4}
                                                        size="xs"
                                                        colorScheme="purple"
                                                        onClick={() => {
                                                            onOpenTransferModal();
                                                            setTransferAllPickups(true);
                                                        }}
                                                    >
                                                        <FaShare /> Transfer All Pickups from {courier?.name}
                                                    </Button>
                                                </Th>
                                            </Tr>
                                        </Thead>

                                        <Droppable droppableId="pickups">
                                            {(provided) => (
                                                <Tbody ref={provided.innerRef} {...provided.droppableProps}>
                                                    {allPickups?.map((pickup, index) => {
                                                        let bg;
                                                        if (pickup.type === "DropOffOnly") bg = dropOffBg;
                                                        else if (pickup.type === "Supplies") bg = suppliesBg;
                                                        else if (pickup.status === "PICKED_UP" && pickup.samplesCount === 0) bg = pickedBg;
                                                        else bg = index % 2 === 0 ? rowBgEven : rowBgOdd;

                                                        return (
                                                            <Draggable
                                                                key={pickup._id}
                                                                draggableId={pickup._id.toString()}
                                                                index={index}
                                                            >
                                                                {(provided) => (
                                                                    <Tr
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        bg={bg}
                                                                        color={textColor}
                                                                        _hover={{ bg: rowHoverBg, color: "white" }}
                                                                        borderBottom="2px solid"
                                                                        borderColor={borderColor}

                                                                    >
                                                                        <Td>
                                                                            <PopoverId place="Pickup" index={index} content={pickup._id} />
                                                                        </Td>
                                                                        <Td>{pickup?.pickupLocation?.name || "N/A"}</Td>
                                                                        <Td>{pickup?.dropOffLocation?.name || "N/A"}</Td>
                                                                        <Td>{pickup.courier?.name}</Td>
                                                                        <Td>
                                                                            <Badge colorScheme={pickupBadgeColor(pickup.type)}>
                                                                                {pickup.type}
                                                                            </Badge>
                                                                        </Td>
                                                                        <Td>{getRequirementsIcons(pickup.requirements)}</Td>
                                                                        <Td>
                                                                            <Badge colorScheme={pickup.routeId?.name ? "red" : "green"}>
                                                                                {pickup.routeId?.name || "On Call"}
                                                                            </Badge>
                                                                        </Td>
                                                                        <Td>
                                                                            <Badge colorScheme="blue">{status(pickup.status)}</Badge>
                                                                        </Td>
                                                                        <Td>
                                                                            {pickup.status === "PENDING" ? <FaClock /> : pickup.samplesCount}
                                                                        </Td>
                                                                        <Td>{new Date(pickup.createdAt).toLocaleTimeString()}</Td>
                                                                        <Td textAlign="right">
                                                                            <HStack justifyContent="flex-end" spacing={4}>
                                                                                {pickup.type === "Default" && (
                                                                                    <Button
                                                                                        size="xs"
                                                                                        colorScheme="green"
                                                                                        onClick={() => navigate(`/pickups/view/${pickup._id}`)}
                                                                                    >
                                                                                        <ViewIcon mr={2} /> View
                                                                                    </Button>
                                                                                )}

                                                                                <Button
                                                                                    size="xs"
                                                                                    colorScheme="purple"
                                                                                    onClick={() => {
                                                                                        setSelectedPickup(pickup);
                                                                                        onOpenTransferModal();
                                                                                        setTransferAllPickups(false);
                                                                                    }}
                                                                                >
                                                                                    <FaShare /> Transfer
                                                                                </Button>
                                                                            </HStack>
                                                                        </Td>
                                                                    </Tr>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </Tbody>
                                            )}
                                        </Droppable>
                                    </Table>
                                </DragDropContext>
                            </>

                        }
                    </>
                }

                <Modal isOpen={isTransferModalOpen} onClose={onCloseTransferModal}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Transfer {transferAllPickups ? "All Pickups" : selectedPickup?.pickupLocation?.name}</ModalHeader>
                        <ModalBody>

                            <Text mb={4}>Select courier to transfer this pickup to</Text>
                            <FormControl isRequired>
                                {/* <AsyncSelect
                                    cacheOptions
                                    loadOptions={fetchCouriersBySearch}
                                    defaultOptions
                                    onChange={(option) => setTransferCourier(option)}  // Set entire option
                                    value={transferCourier || null} // Use the selected option directly
                                    placeholder="Search and select a courier"
                                    closeMenuOnSelect={true}
                                    isClearable
                                /> */}





                                <AsyncSelect
                                    styles={getSelectStyles(coolors)}
                                    classNamePrefix="rs"
                                    cacheOptions
                                    loadOptions={loadOptions}
                                    defaultOptions={couriers}
                                    onChange={(option) => setTransferCourier(option)}
                                    value={transferCourier || null}
                                    placeholder="Search and select a courier"
                                    closeMenuOnSelect
                                    isClearable
                                />

                            </FormControl>
                        </ModalBody>
                        <ModalFooter>
                            <Button mr={5} isLoading={loadingProcess} colorScheme="red" onClick={transferAllPickups ? handleBulkPickupTransfer : handleTransferPickup}>Transfer</Button>
                            <Button onClick={onCloseTransferModal}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

            </VStack >
        </Box >
    );
};

export default CourierPickups;
